import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Button,
  Typography,
  Avatar,
  IconButton,
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import MicIcon from "@material-ui/icons/Mic";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { isLikelyNgrokPublicUrl, fetchPublicMediaBlobUrl } from "../../utils/ngrokPublicMedia";
import { resolveUserAvatar, handleImageErrorSilent } from "../../utils/avatar";

const LS_NAME = "audioMessageRate";
const WAVEFORM_BARS = 40;

const formatAudioTime = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const waveIdle = isDark ? "rgba(134, 150, 160, 0.85)" : "rgba(84, 101, 111, 0.55)";
  const wavePlayed = isDark ? "#53bdeb" : theme.palette.primary.main;

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minWidth: 220,
      maxWidth: 320,
      padding: "2px 0 0",
    },
    playerRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      width: "100%",
    },
    playButton: {
      width: 34,
      height: 34,
      padding: 0,
      flexShrink: 0,
      color: isDark ? "#e9edef" : "#54656f",
    },
    waveformWrap: {
      position: "relative",
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 2,
      height: 30,
      minWidth: 0,
      cursor: "pointer",
      padding: "0 2px",
    },
    waveformBar: {
      flex: 1,
      minWidth: 2,
      maxWidth: 4,
      borderRadius: 2,
      backgroundColor: waveIdle,
      transition: "background-color 0.12s ease, height 0.12s ease",
    },
    waveformBarPlayed: {
      backgroundColor: wavePlayed,
    },
    scrubDot: {
      position: "absolute",
      top: "50%",
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: wavePlayed,
      transform: "translate(-50%, -50%)",
      pointerEvents: "none",
      boxShadow: "0 0 0 1px rgba(255,255,255,0.35)",
    },
    avatarWrap: {
      position: "relative",
      flexShrink: 0,
      width: 40,
      height: 40,
    },
    senderAvatar: {
      width: 40,
      height: 40,
      fontSize: "0.95rem",
    },
    micBadge: {
      position: "absolute",
      left: -2,
      bottom: -2,
      width: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: isDark ? "#202c33" : "#dcf8c6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
      "& svg": {
        fontSize: 12,
        color: wavePlayed,
      },
    },
    metaRow: {
      display: "flex",
      alignItems: "center",
      marginTop: 2,
      paddingLeft: 42,
      paddingRight: 2,
      minHeight: 16,
    },
    durationText: {
      fontSize: 11,
      lineHeight: 1.2,
      color: isDark ? "rgba(233, 237, 239, 0.65)" : "rgba(84, 101, 111, 0.85)",
      fontVariantNumeric: "tabular-nums",
    },
    rateChip: {
      marginLeft: 8,
      fontSize: 10,
      lineHeight: 1,
      padding: "2px 6px",
      minWidth: "auto",
      height: 18,
      borderRadius: 9,
      backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
      color: "inherit",
      textTransform: "none",
      boxShadow: "none",
    },
    transcribeLink: {
      marginTop: 4,
      marginLeft: 42,
      fontSize: 11,
      textTransform: "none",
      padding: 0,
      minWidth: "auto",
      fontWeight: 500,
      alignSelf: "flex-start",
    },
    transcriptionText: {
      marginTop: 6,
      marginLeft: 42,
      fontSize: "0.8125rem",
      lineHeight: 1.35,
      wordBreak: "break-word",
      color: "inherit",
      opacity: 0.92,
    },
    hiddenAudio: {
      display: "none",
    },
  };
});

const AudioModal = ({
  url,
  message,
  disableTranscription = false,
  senderUser,
  companyId,
}) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const [audioRate, setAudioRate] = useState(
    parseFloat(localStorage.getItem(LS_NAME) || "1")
  );
  const [showButtonRate, setShowButtonRate] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const isIOS =
    (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
    /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [waveform, setWaveform] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackBlobUrl, setPlaybackBlobUrl] = useState(null);
  const playbackBlobUrlRef = useRef(null);
  const [ngrokPlaybackUrl, setNgrokPlaybackUrl] = useState(null);
  const ngrokPlaybackUrlRef = useRef(null);

  const body = message?.body ?? "";
  const transcrito = message?.transcrito ?? false;

  const rawUrl = url ? String(url).split("?")[0] : "";
  const usePlaybackEndpoint = isIOS && !!message?.id;

  useEffect(() => {
    if (!usePlaybackEndpoint) {
      setPlaybackBlobUrl(null);
      return;
    }
    api
      .get(`/messages/${message.id}/playback-audio`, { responseType: "blob" })
      .then((res) => {
        if (playbackBlobUrlRef.current) {
          URL.revokeObjectURL(playbackBlobUrlRef.current);
          playbackBlobUrlRef.current = null;
        }
        const blobUrl = URL.createObjectURL(res.data);
        playbackBlobUrlRef.current = blobUrl;
        setPlaybackBlobUrl(blobUrl);
      })
      .catch(() => setPlaybackBlobUrl(null));
    return () => {
      if (playbackBlobUrlRef.current) {
        URL.revokeObjectURL(playbackBlobUrlRef.current);
        playbackBlobUrlRef.current = null;
      }
      setPlaybackBlobUrl(null);
    };
  }, [usePlaybackEndpoint, usePlaybackEndpoint ? message?.id : null]);

  useEffect(() => {
    if (usePlaybackEndpoint || !url) {
      setNgrokPlaybackUrl(null);
      return;
    }
    const raw = String(url);
    if (!isLikelyNgrokPublicUrl(raw)) {
      setNgrokPlaybackUrl(null);
      return;
    }
    let cancelled = false;
    fetchPublicMediaBlobUrl(raw)
      .then((blobUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        if (ngrokPlaybackUrlRef.current) {
          URL.revokeObjectURL(ngrokPlaybackUrlRef.current);
          ngrokPlaybackUrlRef.current = null;
        }
        ngrokPlaybackUrlRef.current = blobUrl;
        setNgrokPlaybackUrl(blobUrl);
      })
      .catch(() => setNgrokPlaybackUrl(null));
    return () => {
      cancelled = true;
      if (ngrokPlaybackUrlRef.current) {
        URL.revokeObjectURL(ngrokPlaybackUrlRef.current);
        ngrokPlaybackUrlRef.current = null;
      }
      setNgrokPlaybackUrl(null);
    };
  }, [url, usePlaybackEndpoint]);

  useEffect(() => {
    if (usePlaybackEndpoint && playbackBlobUrl && audioRef.current) {
      audioRef.current.src = playbackBlobUrl;
      audioRef.current.load();
    }
  }, [usePlaybackEndpoint, playbackBlobUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioRate;
      localStorage.setItem(LS_NAME, audioRate);
    }
  }, [audioRate]);

  useEffect(() => {
    let cancelled = false;
    const dataJson = message?.dataJson;
    if (dataJson && typeof dataJson === "object" && Array.isArray(dataJson.waveform)) {
      setWaveform(dataJson.waveform.slice(0, WAVEFORM_BARS));
      return;
    }
    if (typeof dataJson === "string") {
      try {
        const parsed = JSON.parse(dataJson);
        if (Array.isArray(parsed?.waveform)) {
          setWaveform(parsed.waveform.slice(0, WAVEFORM_BARS));
          return;
        }
      } catch (_) {
        // noop
      }
    }
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (_) {
      setWaveform(Array(WAVEFORM_BARS).fill(8));
      return;
    }
    fetch(url, {
      mode: "cors",
      headers: isLikelyNgrokPublicUrl(String(url))
        ? { "ngrok-skip-browser-warning": "true" }
        : {},
    })
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        if (cancelled || !decoded) return;
        const ch = decoded.getChannelData(0);
        const len = ch.length;
        const seg = Math.floor(len / WAVEFORM_BARS);
        const peaks = [];
        for (let i = 0; i < WAVEFORM_BARS; i++) {
          let peak = 0;
          const start = i * seg;
          for (let j = 0; j < seg && start + j < len; j++) {
            const v = Math.abs(ch[start + j]);
            if (v > peak) peak = v;
          }
          peaks.push(Math.min(255, Math.round(peak * 255) || 8));
        }
        setWaveform(peaks);
      })
      .catch(() => {
        if (!cancelled) setWaveform(Array(WAVEFORM_BARS).fill(8));
      });
    return () => {
      cancelled = true;
    };
  }, [url, message?.dataJson]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(el.duration || 0);
    const onPlay = () => {
      setIsPlaying(true);
      setShowButtonRate(true);
    };
    const onPause = () => {
      setIsPlaying(false);
      setShowButtonRate(false);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setShowButtonRate(false);
      setCurrentTime(0);
    };

    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("durationchange", onDurationChange);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    setDuration(el.duration || 0);
    setCurrentTime(el.currentTime || 0);

    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("durationchange", onDurationChange);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [url, playbackBlobUrl, ngrokPlaybackUrl]);

  const toggleRate = () => {
    const next = { 0.5: 1, 1: 1.5, 1.5: 2, 2: 0.5 };
    setAudioRate(next[audioRate] ?? 1);
  };

  const displayUrl =
    usePlaybackEndpoint && playbackBlobUrl
      ? playbackBlobUrl
      : ngrokPlaybackUrl
        ? ngrokPlaybackUrl
        : url
          ? rawUrl || String(url).split("?")[0]
          : null;
  const displayType = usePlaybackEndpoint && playbackBlobUrl ? "audio/mpeg" : null;

  const getAudioSource = () => {
    if (!displayUrl) return null;
    if (displayType) return <source src={displayUrl} type={displayType} />;
    const lower = displayUrl.toLowerCase();
    let mimeType = "audio/ogg";
    if (lower.endsWith(".mp3")) mimeType = "audio/mpeg";
    else if (lower.endsWith(".m4a") || lower.endsWith(".mp4")) mimeType = "audio/mp4";
    else if (lower.endsWith(".wav")) mimeType = "audio/wav";
    else if (lower.endsWith(".oga") || lower.endsWith(".ogg") || lower.endsWith(".opus"))
      mimeType = "audio/ogg";
    return <source src={displayUrl} type={mimeType} />;
  };

  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    try {
      if (el.paused) {
        await el.play();
      } else {
        el.pause();
      }
    } catch (err) {
      console.error("[AudioModal] play/pause:", err);
    }
  }, []);

  const handleWaveformClick = (event) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    el.currentTime = pct * duration;
    setCurrentTime(el.currentTime);
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    try {
      const { data } = await api.post("/message/transcribeAudio", {
        wid: message.wid,
      });
      if (data) setTranscription(data);
    } catch (error) {
      console.error("Erro ao transcrever áudio:", error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const bars = waveform && waveform.length > 0 ? waveform : Array(WAVEFORM_BARS).fill(8);
  const progressPct = duration > 0 ? currentTime / duration : 0;
  const displayDuration = duration > 0 ? duration : currentTime;
  const avatarSrc = senderUser && companyId ? resolveUserAvatar(senderUser, companyId) : null;

  return (
    <div className={classes.root}>
      <audio ref={audioRef} preload="metadata" className={classes.hiddenAudio}>
        {getAudioSource()}
      </audio>

      <div className={classes.playerRow}>
        <IconButton
          className={classes.playButton}
          onClick={togglePlay}
          aria-label={isPlaying ? "Pausar áudio" : "Reproduzir áudio"}
          size="small"
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <div className={classes.waveformWrap} onClick={handleWaveformClick} role="presentation">
          {bars.map((val, i) => {
            const played = i / bars.length < progressPct;
            const h = Math.max(4, Math.round((val / 255) * 22));
            return (
              <div
                key={i}
                className={`${classes.waveformBar} ${played ? classes.waveformBarPlayed : ""}`}
                style={{ height: h }}
              />
            );
          })}
          {duration > 0 && (
            <span
              className={classes.scrubDot}
              style={{ left: `${Math.min(100, Math.max(0, progressPct * 100))}%` }}
            />
          )}
        </div>

        {senderUser && companyId && (
          <div className={classes.avatarWrap}>
            <Avatar
              className={classes.senderAvatar}
              src={avatarSrc !== "/nopicture.png" ? avatarSrc : undefined}
              alt={senderUser.name || "Atendente"}
              imgProps={{ onError: handleImageErrorSilent }}
            >
              {(senderUser.name || "?").charAt(0).toUpperCase()}
            </Avatar>
            <span className={classes.micBadge}>
              <MicIcon />
            </span>
          </div>
        )}
      </div>

      <div className={classes.metaRow}>
        <Typography component="span" className={classes.durationText}>
          {formatAudioTime(isPlaying || currentTime > 0 ? currentTime : displayDuration)}
        </Typography>
        {showButtonRate && (
          <Button className={classes.rateChip} onClick={toggleRate} size="small" disableRipple>
            {audioRate}x
          </Button>
        )}
      </div>

      {!disableTranscription && (
        <>
          {!transcrito && !transcription && (
            <Button
              onClick={handleTranscribe}
              className={classes.transcribeLink}
              disabled={isTranscribing}
              color="primary"
              size="small"
            >
              {isTranscribing ? "Transcrevendo..." : "Transcrever"}
            </Button>
          )}
          {(transcription || transcrito) && (
            <Typography className={classes.transcriptionText} variant="body2">
              {transcription || body}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default AudioModal;
